/**
 * AiStudioLauncher
 *
 * The modal that opens once a video has been selected. Two views:
 *
 *   "fork"  — the one decision that matters: fill this in by hand, or let AI do it.
 *             Deliberately just two choices, because an operator who came here to
 *             type a title should be able to leave in one click.
 *
 *   "board" — a card per AI task. Selecting a card reveals only that task's own
 *             options (languages, trailer length, frame interval), so the panel
 *             stays flexible without being a wall of settings. The footer always
 *             states the two things an operator actually needs before committing:
 *             how long it will take, and how much AI quota it spends.
 *
 * Stateless with respect to job execution — it collects a selection and hands it
 * to `onStart`. All progress reporting lives in AiStudioDock.
 */

import React, { useMemo, useState } from "react";
import { IoClose, IoCheckmarkCircle, IoChevronDown } from "react-icons/io5";
import { HiSparkles } from "react-icons/hi2";
import { MdOutlineEdit } from "react-icons/md";
import style from "./aiStudio.module.css";
import { taskIcon } from "./taskIcons";
import {
  AI_STUDIO_TASKS,
  AI_TASKS,
  AI_TARGET_LANGUAGES,
  THUMBNAIL_INTERVAL_OPTIONS,
  TRAILER_LENGTH_OPTIONS,
  defaultSelection,
  estimateDurationSeconds,
  estimateGeminiRequests,
  usesTts,
  formatEta,
  languageName,
} from "../../services/ai/aiStudioTasks";

const AiStudioLauncher = ({
  open,
  videoName,
  /** Language codes that already have a subtitle file — shown as ✓ on the chips. */
  existingSubtitleLangs,
  /** Language codes that already have an audio track. */
  existingDubLangs,
  onStart,
  onManual,
  onClose,
}) => {
  const [view, setView] = useState("fork");
  const [selected, setSelected] = useState(() => defaultSelection());
  const [subtitleLanguages, setSubtitleLanguages] = useState([]);
  const [dubLanguages, setDubLanguages] = useState([]);
  const [trailerLength, setTrailerLength] = useState(30);
  const [thumbInterval, setThumbInterval] = useState(10);
  /** Which selected card has its options drawer open. */
  const [expanded, setExpanded] = useState(null);

  const subsDone = existingSubtitleLangs || new Set();
  const dubsDone = existingDubLangs || new Set();

  const isPicked = (id) => selected.includes(id);

  const togglePick = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Opening a card's drawer as it's picked surfaces its options without an
      // extra click; deselecting closes the drawer so it can't linger.
      const hasOptions =
        id === AI_TASKS.SUBTITLES ||
        id === AI_TASKS.DUB ||
        id === AI_TASKS.TRAILER ||
        id === AI_TASKS.THUMBNAILS;
      if (next.includes(id) && hasOptions) setExpanded(id);
      else if (!next.includes(id) && expanded === id) setExpanded(null);
      return next;
    });
  };

  const toggleLang = (setter) => (code) =>
    setter((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const etaSeconds = useMemo(
    () => estimateDurationSeconds(selected, { dubLanguages }),
    [selected, dubLanguages]
  );
  const quotaCost = useMemo(() => estimateGeminiRequests(selected), [selected]);
  const spendsTts = useMemo(() => usesTts(selected), [selected]);
  // True when picking these together costs one transcription instead of two.
  const sharesTranscript = useMemo(
    () => selected.includes(AI_TASKS.SUBTITLES) && selected.includes(AI_TASKS.DUB),
    [selected]
  );

  // Hooks must run on every render, so the closed case bails out only after them.
  if (!open) return null;

  // Dubbing is the one task that cannot run without an explicit language choice.
  const dubMissingLangs = isPicked(AI_TASKS.DUB) && dubLanguages.length === 0;
  const canStart = selected.length > 0 && !dubMissingLangs;

  const handleStart = () => {
    if (!canStart) return;
    onStart?.({
      tasks: selected,
      subtitleLanguages,
      dubLanguages,
      trailerLength,
      thumbInterval,
    });
  };

  /**
   * Chip row used by both the subtitle and dub drawers.
   *
   * Two states have to stay clearly distinct, and originally they didn't: a green tick
   * marked "already saved", which reads as "selected" — an operator picked nothing and
   * believed they had picked two languages. So the tick now belongs ONLY to the chosen
   * state, and "already saved" is a muted word instead of a competing colour.
   */
  const renderLangChips = (values, onToggle, doneSet) => (
    <div className={style.langChips}>
      {AI_TARGET_LANGUAGES.map((lang) => {
        const active = values.includes(lang.code);
        const exists = doneSet.has(lang.code);
        return (
          <button
            key={lang.code}
            type="button"
            className={[
              style.langChip,
              active ? style.langChipActive : "",
              exists && !active ? style.langChipSaved : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onToggle(lang.code)}
            aria-pressed={active}
            title={
              exists
                ? active
                  ? `${lang.name} already exists — generating will replace it`
                  : `${lang.name} is already saved. Select it only to regenerate.`
                : lang.name
            }
          >
            {active && <span className={style.langChipCheck}>✓</span>}
            {lang.name}
            {exists && !active && <span className={style.langChipSavedTag}>saved</span>}
          </button>
        );
      })}
    </div>
  );

  const renderOptions = (taskId) => {
    if (taskId === AI_TASKS.SUBTITLES) {
      return (
        <div className={style.optionsBody}>
          <p className={style.optionsLabel}>
            The spoken language is always transcribed. Pick any extra languages to
            translate into:
          </p>
          {renderLangChips(subtitleLanguages, toggleLang(setSubtitleLanguages), subsDone)}
          {/* Spell out the file COUNT. "Original + English" read as "one English file"
              to an operator whose video was Hindi, who then wondered why two appeared.
              The spoken language always gets its own file — say so in the outcome, not
              just in the instruction above. */}
          <p className={style.optionsNote}>
            {subtitleLanguages.length === 0
              ? "You'll get 1 file — the spoken language."
              : `You'll get ${subtitleLanguages.length + 1} files — the spoken language, plus ${subtitleLanguages
                  .map(languageName)
                  .join(", ")}.`}
          </p>
        </div>
      );
    }

    if (taskId === AI_TASKS.DUB) {
      return (
        <div className={style.optionsBody}>
          <p className={style.optionsLabel}>Languages to speak the dialogue in:</p>
          {renderLangChips(dubLanguages, toggleLang(setDubLanguages), dubsDone)}
          {dubMissingLangs && (
            <p className={style.optionsWarn}>Pick at least one language to dub into.</p>
          )}
          {dubLanguages.length > 1 && (
            <p className={style.optionsNote}>
              Each language is voiced separately — {dubLanguages.length} languages will
              take roughly {dubLanguages.length}× as long.
            </p>
          )}
        </div>
      );
    }

    if (taskId === AI_TASKS.TRAILER) {
      return (
        <div className={style.optionsBody}>
          <p className={style.optionsLabel}>Trailer length:</p>
          <div className={style.segmented}>
            {TRAILER_LENGTH_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={[
                  style.segment,
                  trailerLength === opt.value ? style.segmentActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setTrailerLength(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (taskId === AI_TASKS.THUMBNAILS) {
      const active = THUMBNAIL_INTERVAL_OPTIONS.find((o) => o.value === thumbInterval);
      return (
        <div className={style.optionsBody}>
          <p className={style.optionsLabel}>How often to capture a preview frame:</p>
          <div className={style.segmented}>
            {THUMBNAIL_INTERVAL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={[
                  style.segment,
                  thumbInterval === opt.value ? style.segmentActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setThumbInterval(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {active?.hint && <p className={style.optionsNote}>{active.hint}</p>}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={style.overlay} role="dialog" aria-modal="true" aria-label="AI Studio">
      <div className={`${style.modal} ${view === "board" ? style.modalWide : ""}`}>
        <button
          type="button"
          className={style.modalClose}
          onClick={onClose}
          aria-label="Close"
        >
          <IoClose />
        </button>

        {view === "fork" ? (
          <>
            <div className={style.forkHead}>
              <span className={style.forkSpark}>
                <HiSparkles />
              </span>
              <h2 className={style.forkTitle}>Your video is ready</h2>
              <p className={style.forkSub}>
                {videoName ? (
                  <>
                    <strong className={style.forkFile}>{videoName}</strong> is selected.
                    How do you want to fill in its details?
                  </>
                ) : (
                  "How do you want to fill in the details?"
                )}
              </p>
            </div>

            <div className={style.forkGrid}>
              <button type="button" className={style.forkCard} onClick={onManual}>
                <span className={style.forkCardIcon}>
                  <MdOutlineEdit />
                </span>
                <span className={style.forkCardTitle}>Fill in manually</span>
                <span className={style.forkCardText}>
                  Go straight to the form and type everything in yourself.
                </span>
              </button>

              <button
                type="button"
                className={`${style.forkCard} ${style.forkCardPrimary}`}
                onClick={() => setView("board")}
              >
                <span className={style.forkBadge}>Recommended</span>
                <span className={style.forkCardIcon}>
                  <HiSparkles />
                </span>
                <span className={style.forkCardTitle}>Generate with AI</span>
                <span className={style.forkCardText}>
                  Metadata, captions, dubbing, a trailer and seek-bar previews — pick
                  what you want, keep editing while it runs.
                </span>
              </button>
            </div>

            <p className={style.forkFoot}>
              AI never overwrites something you've already typed, and every value stays
              editable.
            </p>
          </>
        ) : (
          <>
            <div className={style.boardHead}>
              <div>
                <h2 className={style.boardTitle}>
                  <HiSparkles className={style.boardTitleIcon} />
                  What should AI create?
                </h2>
                <p className={style.boardSub}>
                  Pick as many as you like. They run in the background — you can close
                  this and carry on filling the form.
                </p>
              </div>
            </div>

            <div className={style.taskGrid}>
              {AI_STUDIO_TASKS.map((task) => {
                const Icon = taskIcon(task.icon);
                const picked = isPicked(task.id);
                const hasOptions = Boolean(renderOptions(task.id));
                const isOpen = expanded === task.id && picked;
                return (
                  <div
                    key={task.id}
                    className={`${style.taskCard} ${picked ? style.taskCardOn : ""}`}
                  >
                    <button
                      type="button"
                      className={style.taskCardMain}
                      onClick={() => togglePick(task.id)}
                      aria-pressed={picked}
                    >
                      <span className={style.taskCardTop}>
                        <span className={style.taskIcon}>
                          <Icon />
                        </span>
                        <span
                          className={`${style.taskCheck} ${picked ? style.taskCheckOn : ""}`}
                        >
                          {picked && <IoCheckmarkCircle />}
                        </span>
                      </span>
                      <span className={style.taskLabel}>{task.label}</span>
                      <span className={style.taskSummary}>{task.summary}</span>
                      <span className={style.taskMeta}>
                        <span className={style.taskMetaTime}>
                          ~{Math.max(1, Math.round(task.etaSeconds / 60))} min
                        </span>
                        <span
                          className={
                            task.geminiRequests === 0
                              ? style.taskMetaFree
                              : style.taskMetaCost
                          }
                        >
                          {task.geminiRequests === 0
                            ? "No AI quota"
                            : `${task.geminiRequests} AI request${
                                task.geminiRequests === 1 ? "" : "s"
                              }`}
                        </span>
                      </span>
                      {task.note && <span className={style.taskNote}>{task.note}</span>}
                    </button>

                    {hasOptions && picked && (
                      <button
                        type="button"
                        className={style.taskOptionsToggle}
                        onClick={() => setExpanded(isOpen ? null : task.id)}
                        aria-expanded={isOpen}
                      >
                        Options
                        <IoChevronDown
                          className={`${style.taskChevron} ${
                            isOpen ? style.taskChevronOpen : ""
                          }`}
                        />
                      </button>
                    )}
                    {isOpen && renderOptions(task.id)}
                  </div>
                );
              })}
            </div>

            <div className={style.boardFoot}>
              <div className={style.boardStats}>
                {selected.length === 0 ? (
                  <span className={style.boardStatMuted}>Nothing selected yet</span>
                ) : (
                  <>
                    <span className={style.boardStat}>
                      <strong>{selected.length}</strong> selected
                    </span>
                    <span className={style.boardStatDot} />
                    <span className={style.boardStat}>{formatEta(etaSeconds)}</span>
                    {quotaCost > 0 && (
                      <>
                        <span className={style.boardStatDot} />
                        <span className={style.boardStat}>
                          ~<strong>{quotaCost}</strong> AI request
                          {quotaCost === 1 ? "" : "s"}
                          {spendsTts && " + speech"}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Say so when the pairing is what saved a request — otherwise the
                  number just looks lower than the cards add up to. */}
              {sharesTranscript && (
                <p className={style.boardSaving}>
                  Subtitles and dubbing share one transcription — that's 1 request saved.
                </p>
              )}

              <div className={style.boardActions}>
                <button type="button" className={style.btnGhost} onClick={onManual}>
                  Skip, I'll do it manually
                </button>
                <button
                  type="button"
                  className={style.btnPrimary}
                  onClick={handleStart}
                  disabled={!canStart}
                  title={
                    dubMissingLangs
                      ? "Pick at least one language to dub into"
                      : undefined
                  }
                >
                  <HiSparkles />
                  Start generating
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AiStudioLauncher;
